export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function formatApiError(payload: unknown): string | null {
  if (typeof payload === "string") {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const directError = record.error;

  if (typeof directError === "string" && directError.trim()) {
    return directError;
  }

  if (directError && typeof directError === "object") {
    const errorRecord = directError as Record<string, unknown>;
    const formErrors = Array.isArray(errorRecord.formErrors) ? errorRecord.formErrors.filter((value): value is string => typeof value === "string" && value.trim() !== "") : [];
    const fieldErrors = errorRecord.fieldErrors && typeof errorRecord.fieldErrors === "object"
      ? Object.entries(errorRecord.fieldErrors as Record<string, unknown>)
          .flatMap(([field, value]) =>
            Array.isArray(value)
              ? value
                  .filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
                  .map((entry) => `${field}: ${entry}`)
              : []
          )
      : [];

    const combined = [...formErrors, ...fieldErrors];
    if (combined.length) {
      return combined.join(" | ");
    }
  }

  return null;
}

export async function apiFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  const normalizedUrl = String(url ?? "");
  const csrf =
    normalizedUrl.startsWith("/api/customer-auth/") || normalizedUrl.startsWith("/api/customer-portal/")
      ? getCookie("bs_customer_csrf") ?? getCookie("bs_csrf")
      : getCookie("bs_csrf") ?? getCookie("bs_customer_csrf");
  if (csrf && !["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase())) {
    headers.set("x-csrf-token", csrf);
  }

  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers
  });

  if (!response.ok) {
    let message = response.statusText;
    const rawBody = await response.text();

    if (rawBody) {
      try {
        const payload = JSON.parse(rawBody);
        message = formatApiError(payload) ?? rawBody;
      } catch {
        message = rawBody;
      }
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
}
